/**
 * Beregninger for prosesser i pipen (CRM-2.1). Ren kalkylefil, ingen
 * Supabase/React-imports. Vektet verdi gjenbruker `weightedPipeline` fra
 * offer-pipeline-calc.ts på tilbudene som hører til åpne prosesser.
 */
import type { DBOpportunity, OpportunityStage } from "./db-types";
import {
  isExpired,
  weightedPipeline,
  type PipelineOffer,
  type ProbabilityWeights,
  type WeightedPipelineResult,
} from "./offer-pipeline-calc";

export const STAGE_LABELS: Record<OpportunityStage, string> = {
  outreach: "Outreach",
  meeting: "Møte",
  offer: "Tilbud",
  won: "Vunnet",
  lost: "Tapt",
};

/** Stages som vises som kolonner i Pipe. Vunnet og tapt er avsluttet. */
export const OPEN_STAGES: readonly OpportunityStage[] = ["outreach", "meeting", "offer"];

/** Stages brukeren selv kan flytte mellom (alt annet styres av tilbudet). */
export const MOVABLE_STAGES: readonly OpportunityStage[] = ["outreach", "meeting"];

export function isOpenStage(stage: OpportunityStage): boolean {
  return OPEN_STAGES.includes(stage);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Hele døgn siden prosessen kom til nåværende stage (aldri negativt). */
/**
 * Åpne prosesser som ennå ikke har et tilbud («Prosesser uten tilbud» på
 * kundekortet). Tilbud-fasen utelates fordi den vises som tilbudskort.
 */
export function openProcessesWithoutOffer<T extends Pick<DBOpportunity, "stage">>(opportunities: readonly T[]): T[] {
  return opportunities.filter((o) => isOpenStage(o.stage) && o.stage !== "offer");
}

export function daysInStage(stageEnteredAt: string, now: Date): number {
  const entered = new Date(stageEnteredAt).getTime();
  if (Number.isNaN(entered)) return 0;
  return Math.max(0, Math.floor((now.getTime() - entered) / DAY_MS));
}

/** Terskler fra system_config.crm_follow_up_rules (samme nøkler som JSON-en). */
export interface StallThresholds {
  outreach_no_reply_days: number;
}

/**
 * Stillstand i pipen: Outreach som har stått lenger enn terskelen, eller et
 * sendt tilbud som har gått ut på dato (isExpired). Møte-prosesser regnes
 * ikke som stillestående her; de fanges av regelen meeting_no_next_step.
 */
export function isStalled(
  opportunity: Pick<DBOpportunity, "stage" | "stage_entered_at">,
  offer: Pick<PipelineOffer, "status" | "valid_until"> | null | undefined,
  thresholds: StallThresholds,
  now: Date,
): boolean {
  if (opportunity.stage === "outreach") {
    return daysInStage(opportunity.stage_entered_at, now) > thresholds.outreach_no_reply_days;
  }
  if (opportunity.stage === "offer" && offer) {
    return isExpired(offer, now);
  }
  return false;
}

/**
 * Verdi i pipe for en kunde: vektet sum over tilbudene som hører til kundens
 * åpne prosesser i Tilbud-kolonnen. Ukategoriserte tilbud telles i
 * uncategorizedCount (samme semantikk som weightedPipeline).
 */
export function openPipelineValueForCustomer(
  opportunities: Pick<DBOpportunity, "stage" | "offer_id">[],
  offers: PipelineOffer[],
  weights: ProbabilityWeights,
  today: Date,
): WeightedPipelineResult {
  const offerIds = new Set(
    opportunities
      .filter((o) => o.stage === "offer" && o.offer_id)
      .map((o) => o.offer_id as string),
  );
  return weightedPipeline(
    offers.filter((o) => offerIds.has(o.id)),
    weights,
    today,
  );
}

/** Navnet på den prosessen gjelder: kunden, ellers leadet (før første tilbud). */
export function opportunityPartyName(o: { customer?: { name: string } | null; lead?: { name: string } | null }): string {
  return o.customer?.name ?? o.lead?.name ?? "Ukjent kunde";
}
